import { SxProps, Theme } from "@mui/material/"
import useMediaQuery from "@mui/material/useMediaQuery"
import Stack from "@mui/material/Stack"
import IconButton from "@mui/material/IconButton"
import Typography from "@mui/material/Typography"
import { observer } from "mobx-react-lite"
import { useTheme } from "theme/useTheme"
import EditOutlined from "@mui/icons-material/EditOutlined"

export type ProposalSectionEditableProps = { editable?: boolean, onEdit?: () => any }
type ProposalSectionProps = React.PropsWithChildren<{
   sx?: SxProps<Theme>,
   shouldSpanPage?: boolean,
} & ProposalSectionEditableProps // If editable, onEdit must be passed
   & ({ heading?: undefined, subheading?: undefined } | { heading: string, subheading?: string }) // Cannot have subheading without heading
>

export default observer(function ProposalSection({ children, heading, subheading, shouldSpanPage, ...props }: ProposalSectionProps) {
   const theme = useTheme()
   const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
   const isTouchDevice = useMediaQuery('(hover: none)')

   const showSectionEditUi = {
      borderColor: theme.palette.gap.primary['200'],
      '& .edit': {
         display: 'flex',
      }
   }

   return <Stack
      component="section"
      sx={{
         paddingX: { xs: 1, sm: 2 },
         paddingY: { xs: 1, sm: 3 },
         '@media print': {
            breakInside: 'avoid-page',
            ...shouldSpanPage ? {
               borderBottom: 'none',
               breakAfter: 'page',
            } : {},
         },
         ...props.sx,
      }}
   >
      <Stack sx={{
         paddingX: { xs: 1, sm: 3 },
         paddingY: { xs: 1, sm: 3 },
         gap: 6,
         border: '1px solid transparent',
         borderRadius: '16px',
         ...props.editable
            ? isTouchDevice
               ? showSectionEditUi
               : { '&:hover': showSectionEditUi }
            : {},
      }}>
         {!!(heading || subheading || props.editable) && <Stack sx={{ display: { xs: 'flex', sm: 'block' }, alignItems: 'center' }}>
            {props.editable && <IconButton
               className="edit"
               size="small"
               onClick={props.onEdit}
               sx={{
                  display: 'none',
                  alignItems: 'center',
                  justifyContent: 'center',
                  float: 'right',
                  width: '42px',
                  height: '42px',
                  padding: 0,
                  '> *': {
                     fontSize: '20px',
                  },
                  ...isMobile
                     ? { margin: 2 }
                     : {},
               }}
            ><EditOutlined /></IconButton>}
            {heading && <Typography variant="h4" sx={{ mb: .5 }}>{heading}</Typography>}
            {subheading && <Typography variant="body2" sx={{ color: 'secondary.light' }}>{subheading}</Typography>}
         </Stack>}
         {children}
      </Stack>
   </Stack>
})
